import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { appConfirmMessage } from '../../../utils/appConfirm';
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaEye,
  FaTruck,
  FaCheckCircle,
  FaExclamationTriangle,
  FaDollarSign,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { graphqlRequest } from '../../../utils/graphqlRequest';
import { useAdminListOverlay } from '../../../utils/useAdminPageSearch';
import VehicleForm from './VehicleForm';
import VehicleDetailModal from './VehicleDetailModal';
import '../styles/InventoryEquipment.css';

const FIND = `
  query FindAllVehicles {
    findAllVehicles {
      id
      vehicleNumber
      makeModel
      make
      model
      registration
      assignedDriver
      currentSite
      fuelThisMonth
      serviceDueDate
      registrationExpiry
      status
      notes
    }
  }
`;

const REMOVE = `
  mutation RemoveVehicle($id: String!) {
    removeVehicle(id: $id)
  }
`;

function formatDate(raw) {
  if (!raw) return '—';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function formatMoney(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return '—';
  return v.toLocaleString('en-AU', {
    style: 'currency',
    currency: 'AUD',
    maximumFractionDigits: 0,
  });
}

function statusLabel(status) {
  return String(status) === 'ServiceDue' ? 'Service Due' : 'Active';
}

function statusClass(status) {
  return String(status) === 'ServiceDue' ? 'ieq-pill--inservice' : 'ieq-pill--active';
}

function isServiceDue(row) {
  if (String(row.status) === 'ServiceDue') return true;
  if (!row.serviceDueDate) return false;
  const d = new Date(row.serviceDueDate);
  return !Number.isNaN(d.getTime()) && d <= new Date();
}

export default function VehiclesDashboard() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const overlay = useAdminListOverlay(rows);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await graphqlRequest(FIND);
      setRows(Array.isArray(data?.findAllVehicles) ? data.findAllVehicles : []);
    } catch (e) {
      toast.error(e?.message || 'Failed to load vehicles');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const stats = useMemo(() => {
    const total = rows.length;
    const due = rows.filter((r) => isServiceDue(r)).length;
    const active = Math.max(0, total - due);
    const fuel = rows.reduce((sum, r) => sum + (Number(r.fuelThisMonth) || 0), 0);
    return { total, active, due, fuel };
  }, [rows]);

  const nextVehicleNumber = useMemo(() => {
    let max = 0;
    for (const row of rows) {
      const m = String(row.vehicleNumber || '').match(/(\d+)$/);
      if (m) max = Math.max(max, parseInt(m[1], 10) || 0);
    }
    return `VEH-${String(max + 1).padStart(3, '0')}`;
  }, [rows]);

  const handleDelete = async (row) => {
    if (!await appConfirmMessage(`Delete vehicle "${row.vehicleNumber}"?`)) return;
    try {
      await graphqlRequest(REMOVE, { id: row.id });
      toast.success('Vehicle deleted');
      await load();
    } catch (e) {
      toast.error(e?.message || 'Failed to delete');
    }
  };

  return (
    <div className="ieq-page">
      <header className="ieq-header">
        <h1>Vehicles</h1>
        <button
          type="button"
          className="ieq-btn ieq-btn--primary"
          onClick={() => overlay.openAdd()}
        >
          <FaPlus /> Add Vehicle
        </button>
      </header>

      <section className="ieq-stats">
        <article className="ieq-stat">
          <div className="ieq-stat-icon ieq-stat-icon--purple">
            <FaTruck />
          </div>
          <div>
            <div className="ieq-stat-value">{stats.total}</div>
            <div className="ieq-stat-label">TOTAL VEHICLES</div>
          </div>
        </article>
        <article className="ieq-stat">
          <div className="ieq-stat-icon ieq-stat-icon--green">
            <FaCheckCircle />
          </div>
          <div>
            <div className="ieq-stat-value">{stats.active}</div>
            <div className="ieq-stat-label">ACTIVE</div>
          </div>
        </article>
        <article className="ieq-stat">
          <div className="ieq-stat-icon ieq-stat-icon--yellow">
            <FaExclamationTriangle />
          </div>
          <div>
            <div className="ieq-stat-value">{stats.due}</div>
            <div className="ieq-stat-label">SERVICE DUE</div>
          </div>
        </article>
        <article className="ieq-stat">
          <div className="ieq-stat-icon ieq-stat-icon--purple">
            <FaDollarSign />
          </div>
          <div>
            <div className="ieq-stat-value">{formatMoney(stats.fuel)}</div>
            <div className="ieq-stat-label">FUEL THIS MONTH</div>
          </div>
        </article>
      </section>

      <div className="ieq-panel">
        <div className="ieq-table-wrap">
          <table className="ieq-table">
            <thead>
              <tr>
                <th>Vehicle ID</th>
                <th>Make / Model</th>
                <th>Registration</th>
                <th>Driver</th>
                <th>Current Site</th>
                <th>Fuel This Month</th>
                <th>Service Due</th>
                <th>Reg. Expiry</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="10" className="ieq-empty">
                    Loading vehicles…
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan="10" className="ieq-empty">
                    No vehicles found. Click Add Vehicle to create one.
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <button
                        type="button"
                        className="ieq-id"
                        onClick={() => overlay.openView(row)}
                      >
                        {row.vehicleNumber}
                      </button>
                    </td>
                    <td>{row.makeModel || '—'}</td>
                    <td>{row.registration || '—'}</td>
                    <td>{row.assignedDriver || '—'}</td>
                    <td>{row.currentSite || '—'}</td>
                    <td>{formatMoney(row.fuelThisMonth)}</td>
                    <td>{formatDate(row.serviceDueDate)}</td>
                    <td>{formatDate(row.registrationExpiry)}</td>
                    <td>
                      <span className={`ieq-pill ${statusClass(row.status)}`}>
                        {statusLabel(row.status)}
                      </span>
                    </td>
                    <td>
                      <div className="ieq-actions">
                        <button
                          type="button"
                          className="ieq-icon-btn"
                          title="View"
                          onClick={() => overlay.openView(row)}
                        >
                          <FaEye />
                        </button>
                        <button
                          type="button"
                          className="ieq-icon-btn"
                          title="Edit"
                          onClick={() => overlay.openEdit(row)}
                        >
                          <FaEdit />
                        </button>
                        <button
                          type="button"
                          className="ieq-icon-btn danger"
                          title="Delete"
                          onClick={() => handleDelete(row)}
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="ieq-foot">
          Showing {rows.length} vehicle{rows.length === 1 ? '' : 's'}
        </div>
      </div>

      {overlay.showForm && (
        <VehicleForm
          editData={overlay.editing}
          nextVehicleNumber={nextVehicleNumber}
          onClose={overlay.closeOverlay}
          onSaved={load}
        />
      )}

      {overlay.viewing && (
        <VehicleDetailModal
          vehicle={overlay.viewing}
          onClose={overlay.closeOverlay}
          onEdit={overlay.openEdit}
        />
      )}
    </div>
  );
}
